import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { subscriptionService } from "@miplatform/api";
import { MiButton, MiCard } from "@miplatform/components";
import { useFeatureFlagsContext } from "@miplatform/feature-flags";
import {
  IFeatureFlagsResponse,
  ISubscriptionResponse,
  PlatformModules,
} from "@miplatform/types";
import { useSession } from "@mibuildweb/hooks";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";

const MODULE_FEATURE_FLAGS: {
  key: keyof IFeatureFlagsResponse;
  module: PlatformModules;
  label: string;
}[] = [
  {
    key: "miDispatchEnabled",
    module: PlatformModules.MiDispatch,
    label: "MiDispatch",
  },
  {
    key: "miInspectionsEnabled",
    module: PlatformModules.MiInspections,
    label: "MiInspections",
  },
  {
    key: "miSchedulingEnabled",
    module: PlatformModules.MiScheduling,
    label: "MiScheduling",
  },
  {
    key: "miAccountingEnabled",
    module: PlatformModules.MiAccounting,
    label: "MiAccounting",
  },
  {
    key: "miEstimatingEnabled",
    module: PlatformModules.MiEstimating,
    label: "MiEstimating",
  },
  {
    key: "miDayEnabled",
    module: PlatformModules.MiDay,
    label: "MiDay",
  },
  {
    key: "miBuildMobileEnabled",
    module: PlatformModules.MiBuildMobile,
    label: "MiBuildMobile",
  },
];

const MODULE_FEATURE_FLAG_KEYS = new Set(
  MODULE_FEATURE_FLAGS.map((featureFlag) => featureFlag.key)
);

const formatFeatureFlagLabel = (featureFlag: string) =>
  featureFlag
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (value) => value.toUpperCase())
    .trim();

export function TenantConfigurationSystemAdmin() {
  const { classes } = useStyles();
  const {
    session: { tenantId: currentTenantId },
  } = useSession();
  const { getFeatureFlags } = useFeatureFlagsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [tenants, setTenants] = useState<ISubscriptionResponse[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [featureFlags, setFeatureFlags] =
    useState<IFeatureFlagsResponse | null>(null);
  const [moduleAccessResult, setModuleAccessResult] = useState<string | null>(
    null
  );
  const [nonModuleAccessResult, setNonModuleAccessResult] = useState<
    string | null
  >(null);

  const withLoading = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) ?? null,
    [selectedTenantId, tenants]
  );

  const readFeatureFlags = async (tenantId: string) => {
    const response = await subscriptionService.tenantConfig.getFeatureFlags({
      tenantId,
    });
    setFeatureFlags(response.data ?? null);
  };

  const handleLoadTenants = async () => {
    await withLoading(async () => {
      const response = await subscriptionService.subscriptions.getAll();
      setTenants(response.data ?? []);
    });
  };

  useEffect(() => {
    handleLoadTenants();
  }, []);

  const handleSelectTenant = async (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setModuleAccessResult(null);
    setNonModuleAccessResult(null);
    await withLoading(async () => {
      await readFeatureFlags(tenantId);
    });
  };

  const handleToggleModuleAccess = async ({
    tenantId,
    module,
    isEnabled,
  }: {
    tenantId: string;
    module: PlatformModules;
    isEnabled: boolean;
  }) => {
    await withLoading(async () => {
      if (isEnabled) {
        await subscriptionService.tenantConfig.disableModuleAccess({
          tenantId,
          module,
        });
      } else {
        await subscriptionService.tenantConfig.enableModuleAccess({
          tenantId,
          module,
        });
      }
      await readFeatureFlags(tenantId);
      setModuleAccessResult(
        `${isEnabled ? "Disabled" : "Enabled"} ${module} for tenant ${tenantId}`
      );
    });
  };

  const handleToggleNonModuleFeatureFlag = async ({
    tenantId,
    featureFlag,
    isEnabled,
  }: {
    tenantId: string;
    featureFlag: keyof IFeatureFlagsResponse;
    isEnabled: boolean;
  }) => {
    await withLoading(async () => {
      if (isEnabled) {
        await subscriptionService.tenantConfig.disableNonModuleFeatureFlagAccess(
          {
            tenantId,
            featureFlag,
          }
        );
      } else {
        await subscriptionService.tenantConfig.enableNonModuleFeatureFlagAccess(
          {
            tenantId,
            featureFlag,
          }
        );
      }
      await readFeatureFlags(tenantId);
      if (tenantId === currentTenantId) {
        getFeatureFlags();
      }
      setNonModuleAccessResult(
        `${
          isEnabled ? "Disabled" : "Enabled"
        } ${featureFlag} for tenant ${tenantId}`
      );
    });
  };

  const nonModuleFeatureFlags = useMemo(() => {
    if (!featureFlags) {
      return [];
    }

    return Object.entries(featureFlags).filter(([key, value]) => {
      return (
        !MODULE_FEATURE_FLAG_KEYS.has(key as keyof IFeatureFlagsResponse) &&
        typeof value === "boolean"
      );
    }) as [keyof IFeatureFlagsResponse, boolean][];
  }, [featureFlags]);

  return (
    <ConfigPageLayout
      title="System Administration"
      hideOnBack
      loading={isLoading}
    >
      <Box className={classes.container}>
        <MiCard title="Tenants">
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tenant Id</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>{tenant.name}</TableCell>
                    <TableCell>{tenant.domain}</TableCell>
                    <TableCell>{tenant.status}</TableCell>
                    <TableCell>{tenant.id}</TableCell>
                    <TableCell align="right">
                      <MiButton
                        title={
                          selectedTenantId === tenant.id
                            ? "Selected"
                            : "Update Config"
                        }
                        variant="contained"
                        onClick={() => handleSelectTenant(tenant.id)}
                        disabled={isLoading || selectedTenantId === tenant.id}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography>No tenants found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MiCard>

        <MiCard title="Tenant Feature Flags">
          {!selectedTenant && (
            <Typography>
              Select a tenant to view and update its config.
            </Typography>
          )}
          {selectedTenant && (
            <Box className={classes.section}>
              <Typography>
                Selected Tenant:{" "}
                <Typography
                  component="span"
                  variant="inherit"
                  className={classes.emphasis}
                >
                  {selectedTenant.name}
                </Typography>{" "}
                ({selectedTenant.id})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature Flag</TableCell>
                      <TableCell>Module</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MODULE_FEATURE_FLAGS.map((featureFlag) => {
                      const isEnabled = Boolean(
                        featureFlags?.[featureFlag.key]
                      );
                      return (
                        <TableRow key={featureFlag.key}>
                          <TableCell>{featureFlag.key}</TableCell>
                          <TableCell>{featureFlag.label}</TableCell>
                          <TableCell>
                            {isEnabled ? "Enabled" : "Disabled"}
                          </TableCell>
                          <TableCell align="right">
                            <MiButton
                              title={isEnabled ? "Disable" : "Enable"}
                              variant="contained"
                              onClick={() =>
                                handleToggleModuleAccess({
                                  tenantId: selectedTenant.id,
                                  module: featureFlag.module,
                                  isEnabled,
                                })
                              }
                              disabled={isLoading}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {moduleAccessResult && (
                <Typography component="pre" className={classes.result}>
                  {moduleAccessResult}
                </Typography>
              )}
            </Box>
          )}
        </MiCard>

        <MiCard title="Non-Module Feature Flags">
          {!selectedTenant && (
            <Typography>
              Select a tenant to view and update its config.
            </Typography>
          )}
          {selectedTenant && (
            <Box className={classes.section}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature Flag</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nonModuleFeatureFlags.map(([featureFlag, isEnabled]) => (
                      <TableRow key={featureFlag}>
                        <TableCell>
                          {formatFeatureFlagLabel(featureFlag)}
                        </TableCell>
                        <TableCell>
                          {isEnabled ? "Enabled" : "Disabled"}
                        </TableCell>
                        <TableCell align="right">
                          <MiButton
                            title={isEnabled ? "Disable" : "Enable"}
                            variant="contained"
                            onClick={() =>
                              handleToggleNonModuleFeatureFlag({
                                tenantId: selectedTenant.id,
                                featureFlag,
                                isEnabled,
                              })
                            }
                            disabled={isLoading}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {nonModuleFeatureFlags.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography>
                            No non-module feature flags found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {nonModuleAccessResult && (
                <Typography component="pre" className={classes.result}>
                  {nonModuleAccessResult}
                </Typography>
              )}
            </Box>
          )}
        </MiCard>
      </Box>
    </ConfigPageLayout>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflowY: "auto",
    height: "100%",
  },
  section: {
    display: "grid",
    gap: theme.spacing(2),
  },
  emphasis: {
    fontWeight: 800,
    fontSize: theme.fontSize.xlarge,
    border: `4px solid ${theme.palette.primary.main}`,
  },
  result: {
    margin: 0,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey.light,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: theme.fontSize.small,
  },
}));
